// «En marcha ahora», arriba del Taller (Pau 24-10-2026: «ver en vivo, cuando se sube algo o se hace una entrega, en
// qué fase está; simple y visual»). Cada subida de Calypso (a pruebas o a producción) y cada entrega de un agente es una
// fila de pasos que se encienden: hecho (verde, ✓), en marcha (azul, late), te toca (oro: el Allow), falló (rojo),
// saltado (gris). Las fases de las subidas salen del servidor (backend/src/despliegues/en-vivo.ts, cada 10 s mientras se
// mira); las de las entregas, del paso de cierre de cada agente (ya en el Taller). Horas en hora de Tailandia.
import { Check, Minus, X } from 'lucide-react';
import { t, type TKey } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { IconoEncargo } from '../ui';
import { edad } from './formato';
import { cuandoPau } from './Obra';
import type { EstadoFaseSubida, SubidaEnVivo, SubidasSala, TallerSala, TrabajoTaller } from './tipos';
import { useSalaRincon } from './useSalaRincon';

export const CADA_MS_EN_VIVO = 10_000;

type Paso = { clave: string; nombre: string; estado: EstadoFaseSubida; detalle: string | null };

const segundos = (desde: string, hasta: number) => Math.max(0, (hasta - Date.parse(desde)) / 1000);

/** «3 min» de lo que duró una fase, o «hace 3 min» de la que está en marcha. */
function detalleFase(f: SubidaEnVivo['fases'][number], ahora: number): string | null {
  if (f.desde && f.hasta) return edad(segundos(f.desde, Date.parse(f.hasta)));
  if (f.desde && (f.estado === 'en_curso' || f.estado === 'esperando_tu')) return t('salaControl.enMarcha.desde', { edad: edad(segundos(f.desde, ahora)) });
  return null;
}

/** La fila de pasos: círculos unidos por una línea que se pinta de verde hasta donde se ha llegado. */
export function Pasos({ pasos, compacto = false }: { pasos: Paso[]; compacto?: boolean }) {
  // Compacto: una fila de puntos (la lista de las últimas subidas). Si no: en el móvil, de arriba abajo (seis pasos no
  // caben en una fila de 390 px); desde sm, de izquierda a derecha.
  const vertical = !compacto;
  return (
    <ol
      className={cn(compacto ? 'grid grid-flow-col gap-1' : 'flex flex-col gap-3 sm:grid sm:gap-1')}
      style={{ gridTemplateColumns: `repeat(${pasos.length}, minmax(0, 1fr))` }}
    >
      {pasos.map((p, i) => {
        const siguiente = pasos[i + 1];
        const linea = p.estado === 'hecho' || p.estado === 'saltado' ? 'bg-oficina-trabajando/60' : 'bg-oficina-linea';
        return (
          <li
            key={p.clave}
            className={cn('relative flex items-center', vertical ? 'gap-3 sm:flex-col sm:gap-0 sm:text-center' : 'flex-col')}
            aria-label={`${p.nombre}: ${t(`salaControl.enMarcha.estados.${p.estado}`)}`}
          >
            {siguiente ? (
              <span
                aria-hidden
                className={cn(
                  'absolute',
                  linea,
                  compacto ? 'left-1/2 top-2 h-0.5 w-full' : 'left-3.5 top-7 h-[calc(100%-0.5rem)] w-0.5 sm:left-1/2 sm:top-3.5 sm:h-0.5 sm:w-full',
                )}
              />
            ) : null}
            <Circulo estado={p.estado} pequeno={compacto} />
            {compacto ? null : (
              <span className="flex min-w-0 flex-col sm:items-center">
                <span className={cn('text-o-sub font-semibold leading-tight sm:mt-1.5 sm:text-o-nota', { pendiente: 'text-oficina-texto3', en_curso: 'text-oficina-serie-1', esperando_tu: 'text-oficina-acento', hecho: 'text-oficina-texto', fallo: 'text-oficina-error-claro', saltado: 'text-oficina-texto3' }[p.estado])}>
                  {p.nombre}
                </span>
                <span className="text-o-nota text-oficina-texto3 sm:text-o-mini">{p.estado === 'esperando_tu' ? t('salaControl.enMarcha.teToca') : (p.detalle ?? t(`salaControl.enMarcha.estados.${p.estado}`))}</span>
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function Circulo({ estado, pequeno }: { estado: EstadoFaseSubida; pequeno: boolean }) {
  const tam = pequeno ? 'h-4 w-4' : 'h-7 w-7';
  const icono = pequeno ? 'h-2.5 w-2.5' : 'h-4 w-4';
  return (
    <span
      aria-hidden
      className={cn(
        'relative z-10 flex shrink-0 items-center justify-center rounded-full',
        tam,
        {
          pendiente: 'border-2 border-oficina-linea bg-oficina-elevada',
          en_curso: 'bg-oficina-serie-1 ring-4 ring-oficina-serie-1/25 motion-safe:animate-pulse',
          esperando_tu: 'bg-oficina-acento ring-4 ring-oficina-acento/30 motion-safe:animate-pulse',
          hecho: 'bg-oficina-trabajando',
          fallo: 'bg-oficina-error',
          saltado: 'border-2 border-oficina-texto3 bg-oficina-elevada',
        }[estado],
      )}
    >
      {estado === 'hecho' ? <Check className={cn(icono, 'text-oficina-trabajando-tinta')} strokeWidth={3} /> : null}
      {estado === 'fallo' ? <X className={cn(icono, 'text-oficina-acento-tinta')} strokeWidth={3} /> : null}
      {estado === 'saltado' ? <Minus className={cn(icono, 'text-oficina-texto3')} strokeWidth={3} /> : null}
    </span>
  );
}

const pasosDeSubida = (s: SubidaEnVivo, ahora: number): Paso[] =>
  s.fases.map((f) => ({ clave: f.clave, nombre: t(`salaControl.enMarcha.fases.${f.clave}` as TKey), estado: f.estado, detalle: detalleFase(f, ahora) }));

/** Los pasos de la entrega de un agente, a partir de en qué paso de cierre está. */
const ORDEN_ENTREGA = ['comprueba', 'revisa', 'prepara', 'cola', 'dentro'] as const;
const INDICE_CIERRE: Record<NonNullable<TrabajoTaller['cierre']>, number> = {
  esperando_informe: 0,
  comprobando: 0,
  revisando: 1,
  esperando_permiso: 2,
  publicando: 2,
  en_cola: 3,
  archivando: 4,
};
export function pasosDeEntrega(w: TrabajoTaller): Paso[] {
  const actual = w.cierre ? INDICE_CIERRE[w.cierre] : 0;
  const atascada = w.paso === 'atascado';
  return ORDEN_ENTREGA.map((clave, i) => ({
    clave,
    nombre: t(`salaControl.enMarcha.entrega.${clave}`),
    estado: i < actual || (clave === 'dentro' && i === actual) ? 'hecho' : i === actual ? (atascada ? 'fallo' : 'en_curso') : 'pendiente',
    detalle: null,
  }));
}

const ETIQUETA_DESTINO = {
  produccion: 'bg-oficina-burbuja-pau text-oficina-acento',
  pruebas: 'bg-oficina-serie-1/15 text-oficina-serie-1',
  entrega: 'bg-oficina-trabajando/15 text-oficina-trabajando',
};

function Tarjeta({ etiqueta, tono, titulo, sub, children, enlace, icono }: { etiqueta: string; tono: keyof typeof ETIQUETA_DESTINO; titulo: string; sub: string; children: React.ReactNode; enlace?: string; icono?: React.ReactNode }) {
  return (
    <li className="rounded-o-s bg-oficina-panel px-3 py-3">
      <div className="mb-3 flex items-start gap-2.5">
        {icono}
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2">
            <span className={cn('rounded-full px-2 py-0.5 text-o-mini font-bold', ETIQUETA_DESTINO[tono])}>{etiqueta}</span>
            <span className="text-o-nota text-oficina-texto3">{sub}</span>
          </p>
          <p className="mt-1 line-clamp-2 text-o-texto font-medium" title={titulo}>
            {titulo}
          </p>
        </div>
        {enlace ? (
          <a href={enlace} target="_blank" rel="noreferrer" className="inline-flex min-h-11 shrink-0 items-center text-o-nota text-oficina-enlace hover:underline sm:min-h-0">
            {t('salaControl.taller.verEnGitHub')}
          </a>
        ) : null}
      </div>
      {children}
    </li>
  );
}

export function EnMarchaVista({ subidas, taller, ahora = Date.now() }: { subidas: SubidasSala | null; taller: TallerSala | null; ahora?: number }) {
  const entregas = (taller?.salas ?? []).flatMap((s) => s.trabajos.filter((w) => w.cierre && w.agente?.vivo).map((w) => ({ w, sala: s.nombre })));
  const vivas = subidas?.enMarcha ?? [];
  const recientes = (subidas?.recientes ?? []).slice(0, 5);
  const nada = !vivas.length && !entregas.length;
  const ultimaProd = subidas?.recientes.find((s) => s.destino === 'produccion');
  return (
    <section className="rounded-o-m border border-oficina-linea bg-oficina-elevada px-4 py-3" data-testid="taller-en-marcha">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3">
        <h4 className="flex items-center gap-2 text-o-cuerpo font-semibold">
          <span aria-hidden className={cn('h-2.5 w-2.5 rounded-full', nada ? 'bg-oficina-texto3' : 'bg-oficina-serie-1 motion-safe:animate-pulse')} />
          {t('salaControl.enMarcha.titulo')}
        </h4>
        <span className="text-o-nota text-oficina-texto3">{t('salaControl.enMarcha.sub')}</span>
      </div>
      {nada ? (
        <p className="text-o-texto text-oficina-texto2">
          {t('salaControl.enMarcha.nada')}
          {ultimaProd ? ` ${t('salaControl.enMarcha.ultimaProd', { cuando: cuandoPau(ultimaProd.termino ?? ultimaProd.empezo), como: t(`salaControl.enMarcha.resultado.${ultimaProd.estado}`) })}` : ''}
        </p>
      ) : (
        <ul className="grid grid-cols-1 gap-2 lg:grid-cols-2">
          {vivas.map((s) => (
            <Tarjeta
              key={s.id}
              etiqueta={t(`salaControl.enMarcha.destino.${s.destino}`)}
              tono={s.destino}
              titulo={s.titulo}
              sub={t('salaControl.enMarcha.empezo', { edad: edad(segundos(s.empezo, ahora)) })}
              enlace={s.url}
            >
              <Pasos pasos={pasosDeSubida(s, ahora)} />
            </Tarjeta>
          ))}
          {entregas.map(({ w, sala }) => (
            <Tarjeta
              key={w.id}
              etiqueta={t('salaControl.enMarcha.destino.entrega')}
              tono="entrega"
              titulo={w.titulo}
              sub={sala}
              icono={<IconoEncargo icono={w.agente?.icono ?? null} semilla={w.agente?.semilla ?? w.id} tamano={36} />}
            >
              <Pasos pasos={pasosDeEntrega(w)} />
            </Tarjeta>
          ))}
        </ul>
      )}
      {recientes.length ? (
        <div className="mt-3">
          <p className="mb-1.5 text-o-nota font-semibold text-oficina-texto3">{t('salaControl.enMarcha.recientes')}</p>
          <ul className="flex flex-col gap-1.5">
            {recientes.map((s) => (
              <li key={s.id} className="grid grid-cols-[auto_1fr_auto] items-center gap-3 text-o-sub sm:grid-cols-[auto_1fr_10rem_auto]">
                <span className={cn('rounded-full px-2 py-0.5 text-o-mini font-bold', ETIQUETA_DESTINO[s.destino])}>{t(`salaControl.enMarcha.destino.${s.destino}`)}</span>
                <a href={s.url} target="_blank" rel="noreferrer" className="min-w-0 truncate hover:underline" title={s.titulo}>
                  {s.titulo}
                </a>
                <span className="hidden sm:block">
                  <Pasos pasos={pasosDeSubida(s, ahora)} compacto />
                </span>
                <span className={cn('col-span-2 whitespace-nowrap pl-1 text-o-nota font-semibold sm:col-span-1 sm:pl-0', { hecho: 'text-oficina-trabajando', fallo: 'text-oficina-error-claro', cancelado: 'text-oficina-texto3', en_curso: '', esperando_tu: '' }[s.estado])}>
                  {t(`salaControl.enMarcha.resultado.${s.estado}`)} · {cuandoPau(s.termino ?? s.empezo)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

/** El bloque con sus datos: las subidas cada 10 s; las entregas, de la foto del Taller que ya tiene la pestaña. */
export function EnMarcha({ taller }: { taller: TallerSala | null }) {
  const { foto } = useSalaRincon<SubidasSala>('subidas', CADA_MS_EN_VIVO);
  return <EnMarchaVista subidas={foto?.datos ?? null} taller={taller} />;
}
